<template>
  <section class="careers" id="open-roles">
    <div class="section-container-header-box fade-up-animation">
      <p class="list-text">Open roles</p>
    </div>
    <h2 class="careers-heading">
      <template v-if="hasOpenings">
        <p class="extra-large-text-list fade-up-animation">A few seats</p>
        <p class="extra-large-text-list special-text fade-up-animation">open right now.</p>
      </template>
      <template v-else>
        <p class="extra-large-text-list fade-up-animation">No seats open</p>
        <p class="extra-large-text-list special-text fade-up-animation">at the moment.</p>
      </template>
    </h2>
    <p class="careers-intro body-text fade-up-animation">
      We hire in small waves, so the roles here change every few months — sometimes there are none.
      {{ hasOpenings ? 'See something that fits, or close to it? Reach out anyway.' : 'Nothing open today, but good people are always worth a conversation.' }}
    </p>

    <!-- Quiet stretch: no openings in the current rotation. -->
    <div v-if="!hasOpenings" class="careers-empty fade-up-animation">
      <p class="body-text">
        We're not actively hiring right now, but we keep great CVs on file and reach out when a wave opens up.
      </p>
      <nuxt-link
        class="arrow-button careers-empty-cta"
        :to="speculativeApplyLink"
        aria-label="Send us your CV">
        <p class="heading-4-bold">Send us your CV</p>
        <div class="arrow-button-icon" v-html="[icons.rightArrowIcon, icons.rightArrowIcon]"></div>
      </nuxt-link>
    </div>

    <div v-else class="careers-list">
      <article v-for="(job, index) in activeJobs" :id="job.slug" :key="job.slug" class="careers-item fade-up-animation">
        <aside class="careers-item-aside">
          <p class="small-text-list text-uppercase">{{ String(index + 1).padStart(2, '0') }} · {{ job.team }}</p>
          <h3 class="heading-3">{{ job.title }}</h3>
          <div class="careers-item-meta">
            <span class="careers-item-tag">{{ job.type }}</span>
            <span class="careers-item-tag">{{ job.location }}</span>
          </div>
          <p class="body-text careers-item-tagline">{{ job.tagline }}</p>
        </aside>

        <div class="careers-item-main">
          <p class="body-text">{{ job.description }}</p>

          <div class="careers-item-columns">
            <div class="careers-item-column">
              <p class="small-text-list text-uppercase">What you'll do</p>
              <ul class="careers-item-points">
                <li v-for="point in job.responsibilities" :key="point" class="careers-item-point">
                  <span class="careers-item-point-marker"></span>
                  <p class="body-text">{{ point }}</p>
                </li>
              </ul>
            </div>
            <div class="careers-item-column">
              <p class="small-text-list text-uppercase">What we're after</p>
              <ul class="careers-item-points">
                <li v-for="point in job.requirements" :key="point" class="careers-item-point">
                  <span class="careers-item-point-marker"></span>
                  <p class="body-text">{{ point }}</p>
                </li>
              </ul>
            </div>
          </div>

          <nuxt-link
            class="arrow-button careers-item-apply"
            :to="applyLink(job)"
            :aria-label="`Apply for ${job.title}`">
            <p class="heading-4-bold">Apply for this role</p>
            <div class="arrow-button-icon" v-html="[icons.rightArrowIcon, icons.rightArrowIcon]"></div>
          </nuxt-link>
        </div>
      </article>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { useFadeUpObserver } from '@/composables/useFadeUpObserver';
import icons from '~/icons';
import { getActiveJobs, type Job } from '@/data/jobs';

// Date-driven roster, resolved on the server and reused on the client via the
// payload (useState) so hydration matches exactly — no mismatch, and no risk of
// cards rendering after the fade-up observer has already scanned the DOM. The
// roster advances with each rebuild/SSR, which is the right cadence for a
// quarterly rotation on a redeployed marketing site.
const activeJobs = useState<Job[]>('careers-active-jobs', () => getActiveJobs(new Date()));

const hasOpenings = computed(() => activeJobs.value.length > 0);

const applyLink = (job: Job) =>
  `mailto:hello@vortec.io?subject=${encodeURIComponent(`Application: ${job.title}`)}`;

const speculativeApplyLink = `mailto:hello@vortec.io?subject=${encodeURIComponent('Speculative application')}`;

useFadeUpObserver();
</script>
